// Does a glTF/GLB payload DECLARE meshopt compression? PURE (no THREE / no DOM) so it
// unit-tests headlessly — same split as _ktx2-texture-config.js.
//
// Why this predicate exists at all: the meshopt decoder is loaded LAZILY (it costs 29.7 KB
// and zero of the 58 live catalogue GLBs use the extension today — see _gltf-loader-factory.js).
// Lazy loading opens exactly one race: a meshopt GLB arriving before the decoder does, which
// GLTFLoader answers by THROWING rather than degrading. This predicate is what closes it —
// a payload that declares meshopt waits for the decoder; one that does not pays nothing.
//
// It reads the GLB's JSON chunk and nothing else. It does NOT JSON.parse: a substring scan of
// the chunk is enough to decide "might use meshopt", and the cost of a false positive is one
// awaited import while the cost of a false negative is a thrown load.

const GLTF_MAGIC = 0x46546c67;   // 'glTF'
const JSON_CHUNK = 0x4e4f534a;   // 'JSON'

/**
 * @param {ArrayBuffer|string} data — GLB bytes, or glTF JSON text
 * @returns {boolean} true when the payload may use EXT_/KHR_meshopt_compression.
 *   Deliberately fails SAFE: an unreadable or non-GLB ArrayBuffer returns true, because
 *   waiting for a decoder that turns out to be unnecessary costs a promise, while
 *   skipping one that was necessary costs the whole load.
 */
export function declaresMeshopt(data) {
	if (typeof data === "string") return data.includes("meshopt");
	if (!data || typeof data.byteLength !== "number") return false;
	try {
		const dv = new DataView(data);
		if (dv.byteLength < 20) return true;
		if (dv.getUint32(0, true) !== GLTF_MAGIC) return true;
		if (dv.getUint32(16, true) !== JSON_CHUNK) return true;
		const chunkLen = dv.getUint32(12, true);
		const end = Math.min(20 + chunkLen, dv.byteLength);
		if (end <= 20) return true;
		return new TextDecoder().decode(new Uint8Array(data, 20, end - 20)).includes("meshopt");
	} catch {
		return true;
	}
}
